import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Building2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { KolPic } from "@/types/table";
import { SearchableSelect } from "@/components/ui/searchable-select";

interface ModifyBuDialogProps {
  user: KolPic;
  onBuChanged: () => Promise<void>;
}

export function ModifyBuDialog({ user, onBuChanged }: ModifyBuDialogProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBUs, setSelectedBUs] = useState<string[]>([]);
  const [newBU, setNewBU] = useState("");
  const [availableContentIds, setAvailableContentIds] = useState<string[]>([]);
  const [isLoadingContentIds, setIsLoadingContentIds] = useState(false);
  const { toast } = useToast();

  // Helper function to safely parse JSONB BU data
  const parseBuData = (buData: any): string[] => {
    if (!buData) return [];
    if (Array.isArray(buData)) return buData.map(item => String(item));
    if (typeof buData === 'string') {
      try {
        const parsed = JSON.parse(buData);
        if (Array.isArray(parsed)) return parsed.map(item => String(item));
        return [buData];
      } catch (e) {
        return [buData];
      }
    }
    return [];
  };

  // Fetch available content IDs
  const fetchContentIds = async () => {
    try {
      setIsLoadingContentIds(true);
      const { data, error } = await supabase
        .from('topics_business_units')
        .select('content_id')
        .not('content_id', 'is', null)
        .order('content_id');

      if (error) throw error;

      const uniqueContentIds = [...new Set(data?.map(item => item.content_id).filter(Boolean) || [])];
      setAvailableContentIds(uniqueContentIds);
    } catch (err) {
      console.error("Failed to fetch content IDs:", err);
      toast({
        title: "Error",
        description: "Failed to load available business units",
        variant: "destructive",
      });
    } finally {
      setIsLoadingContentIds(false);
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      const currentBUs = parseBuData(user.bu);
      setSelectedBUs(currentBUs);
      fetchContentIds();
    }
  }, [dialogOpen, user.bu]);

  const handleAddBU = () => {
    if (newBU.trim() && !selectedBUs.includes(newBU.trim())) {
      setSelectedBUs([...selectedBUs, newBU.trim()]);
      setNewBU("");
    }
  };

  const handleRemoveBU = (buToRemove: string) => {
    setSelectedBUs(selectedBUs.filter(bu => bu !== buToRemove));
  };

  const handleUpdateBU = async () => {
    try {
      setIsUpdating(true);
      
      // Ensure we have proper authentication context
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to perform this action",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Updating BU for user:', user.id, 'with BUs:', selectedBUs);
      console.log('Current auth user:', authUser.id);
      
      const buArrayToUpdate = selectedBUs.length > 0 ? selectedBUs : null;
      
      const { error } = await supabase
        .from('kol_pic')
        .update({ 
          bu: buArrayToUpdate,
          updated_at: new Date().toISOString()
        })
        .eq('auth_id', user.auth_id);
      
      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      
      toast({
        title: "Success",
        description: `Business units updated for ${user.name}`,
      });
      
      setDialogOpen(false);
      await onBuChanged();
      
    } catch (err: any) {
      console.error("Failed to update business units:", err);
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update business units. Please check your permissions.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-20 text-xs">
          <Building2 className="h-3 w-3 mr-1" />
          BU
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Modify Business Units</DialogTitle>
          <DialogDescription>
            Update business unit assignments for {user.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Add Business Unit</label>
            <div className="flex gap-2">
              <SearchableSelect
                options={availableContentIds.filter(id => !selectedBUs.includes(id))}
                value={newBU}
                onChange={setNewBU}
                placeholder="Search business units..."
                className="flex-1"
                disabled={isLoadingContentIds}
              />
              <Button 
                onClick={handleAddBU}
                disabled={!newBU || selectedBUs.includes(newBU) || isLoadingContentIds}
                size="sm"
              >
                Add
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Current Business Units ({selectedBUs.length})
            </label>
            {selectedBUs.length > 0 ? (
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border rounded">
                {selectedBUs.map((bu) => (
                  <Badge key={bu} variant="secondary" className="text-xs">
                    {bu}
                    <X 
                      className="h-3 w-3 ml-1 cursor-pointer hover:text-red-500" 
                      onClick={() => handleRemoveBU(bu)}
                    />
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No business units assigned
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleUpdateBU} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
